import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { ReactNode, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { SEARCH_CONSOLE_DAYS } from './palo-search-console';
import { SearchFunnel, SearchQualityFigures, SearchQualityOverview } from '../models/search-quality';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const percent = (value: number | null | undefined): string =>
    value === null || value === undefined ? '–' : `${(value * 100).toFixed(1).replace('.', ',')} %`;

const count = (value: number): string => value.toLocaleString('de-AT');

interface TileProps {
    label: ReactNode;
    value: string;
    hint?: ReactNode;
    tone?: 'ok' | 'warn' | 'crit';
}

/**
 * One headline figure. The tone only colours the value; the tile itself stays on
 * the surface colour so a wall of alarms does not turn the screen red.
 */
const Tile = ({ label, value, hint, tone }: TileProps) => (
    <Box
        sx={{
            flex: '1 1 150px',
            minWidth: 150,
            padding: '12px 14px',
            backgroundColor: tokens.color.surface,
            border: `1px solid ${tokens.color.line}`,
            borderRadius: '8px',
        }}
    >
        <Box sx={{ fontSize: '11.5px', color: tokens.color.ink2, marginBottom: '4px' }}>{label}</Box>
        <Box
            sx={{
                fontSize: '22px',
                fontWeight: 640,
                letterSpacing: '-.02em',
                fontVariantNumeric: 'tabular-nums',
                color:
                    tone === 'crit'
                        ? tokens.color.crit
                        : tone === 'warn'
                        ? tokens.color.warn
                        : tone === 'ok'
                        ? tokens.color.ok
                        : tokens.color.ink,
            }}
        >
            {value}
        </Box>
        {hint && <Box sx={{ fontSize: '11px', color: tokens.color.ink3, marginTop: '2px' }}>{hint}</Box>}
    </Box>
);

/**
 * Ansicht 1 — Lage. The figures split by what kind of thing was typed: an average
 * over all searches hides exactly the regressions worth finding, because the
 * article number search working says nothing about the word search.
 */
export const SearchSituationView = () => {
    const dispatch = useThunkDispatch();
    const [overview, setOverview] = useState<SearchQualityOverview | null>(null);
    const [funnel, setFunnel] = useState<SearchFunnel | null>(null);
    const [requesting, setRequesting] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setRequesting(true);

        void dispatch(async (_dispatch, _getState, { api }) => {
            const [overviewResult, funnelResult] = await Promise.all([
                api.searchQuality.getOverview(SEARCH_CONSOLE_DAYS),
                api.searchQuality.getFunnel(SEARCH_CONSOLE_DAYS),
            ]);
            if (cancelled) {
                return;
            }
            setOverview(overviewResult.success ? overviewResult.result : null);
            setFunnel(funnelResult.success ? funnelResult.result : null);
            setRequesting(false);
        });

        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    const total = overview?.total;
    const rows = overview?.byQueryType ?? [];

    const columns: PaloColumn<SearchQualityFigures>[] = [
        {
            key: 'queryType',
            type: 'classification',
            head: <FormattedMessage id="app.searchConsole.column.queryType" />,
            cell: row => <FormattedMessage id={`app.searchConsole.queryType.${row.queryType ?? 'all'}`} />,
        },
        {
            key: 'share',
            head: <FormattedMessage id="app.searchConsole.column.share" />,
            align: 'right',
            variant: 'amount',
            cell: row => percent(row.share),
        },
        {
            key: 'searches',
            head: <FormattedMessage id="app.searchConsole.column.searches" />,
            align: 'right',
            variant: 'amount',
            cell: row => count(row.searches),
        },
        {
            key: 'withoutResults',
            head: <FormattedMessage id="app.searchConsole.column.withoutResults" />,
            align: 'right',
            variant: 'amount',
            cell: row => percent(row.withoutResults),
        },
        {
            key: 'medianResultCount',
            head: <FormattedMessage id="app.searchConsole.column.medianResults" />,
            align: 'right',
            variant: 'amount',
            cell: row => count(row.medianResultCount),
        },
        {
            key: 'firstHitMatches',
            head: <FormattedMessage id="app.searchConsole.column.firstHitMatches" />,
            align: 'right',
            variant: 'amount',
            cell: row => percent(row.firstHitMatches),
        },
        {
            key: 'clickRate',
            head: <FormattedMessage id="app.searchConsole.column.clickRate" />,
            align: 'right',
            variant: 'amount',
            cell: row => percent(row.clickRate),
        },
        {
            key: 'medianClickPosition',
            head: <FormattedMessage id="app.searchConsole.column.medianClickPosition" />,
            align: 'right',
            variant: 'amount',
            cell: row => (row.medianClickPosition === null ? '–' : count(row.medianClickPosition)),
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                <FormattedMessage
                    id="app.searchConsole.situation.period"
                    values={{ days: SEARCH_CONSOLE_DAYS, from: overview?.from ?? '–', to: overview?.to ?? '–' }}
                />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <Tile
                    label={<FormattedMessage id="app.searchConsole.tile.searches" />}
                    value={total ? count(total.searches) : '–'}
                />
                <Tile
                    label={<FormattedMessage id="app.searchConsole.tile.withoutResults" />}
                    value={percent(total?.withoutResults)}
                    tone={total && total.withoutResults > 0.05 ? 'warn' : undefined}
                />
                <Tile
                    label={<FormattedMessage id="app.searchConsole.tile.firstHitMatches" />}
                    value={percent(total?.firstHitMatches)}
                />
                <Tile
                    label={<FormattedMessage id="app.searchConsole.tile.clickRate" />}
                    value={percent(total?.clickRate)}
                />
                <Tile
                    label={<FormattedMessage id="app.searchConsole.tile.failureRate" />}
                    value={percent(funnel?.total.failureRate)}
                    hint={
                        funnel && (
                            <FormattedMessage
                                id="app.searchConsole.tile.failureRate.hint"
                                values={{ minutes: funnel.windowMinutes, searches: count(funnel.total.searches) }}
                            />
                        )
                    }
                    tone={funnel && funnel.total.failureRate > 0.5 ? 'crit' : undefined}
                />
                <Tile
                    label={<FormattedMessage id="app.searchConsole.tile.errors" />}
                    value={total ? count(total.errors) : '–'}
                    tone={total && total.errors > 0 ? 'crit' : undefined}
                />
            </Box>

            <PaloListView
                embedded={true}
                columns={columns}
                items={rows}
                getRowId={row => row.queryType ?? 'all'}
                requesting={requesting}
                emptyLabel={<FormattedMessage id="app.searchConsole.situation.empty" />}
            />
        </Box>
    );
};
